import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Menu, X, ChevronRight } from "lucide-react";
import LanguageCurrencySelector from "../components/LanguageCurrencySelector";
import PromoBanner from "../components/PromoBanner";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [receivedInterests, setReceivedInterests] = useState([]);
  const [sentInterests, setSentInterests] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [showPromoBanner, setShowPromoBanner] = useState(true);
  const logoClickTimeout = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Secret admin access - click logo 5 times quickly
  const handleLogoClick = () => {
    setLogoClickCount(prev => prev + 1);
    
    if (logoClickTimeout.current) {
      clearTimeout(logoClickTimeout.current);
    }
    
    logoClickTimeout.current = setTimeout(() => {
      setLogoClickCount(0);
    }, 2000); // Reset after 2 seconds of no clicks
    
    if (logoClickCount + 1 >= 5) {
      navigate('/admin');
      setLogoClickCount(0);
    }
  };

  const fetchData = async () => {
    try {
      const [receivedRes, sentRes, quoteRes] = await Promise.all([
        axios.get(`${API}/interests/received`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/interests/sent`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/quotes/random`)
      ]);
      setReceivedInterests(receivedRes.data);
      setSentInterests(sentRes.data);
      setQuote(quoteRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInterest = async (interestId) => {
    try {
      await axios.post(`${API}/interests/${interestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Interest accepted!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error accepting interest");
    }
  };

  const handleDeclineInterest = async (interestId) => {
    try {
      await axios.post(`${API}/interests/${interestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Interest declined");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error declining interest");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const pendingInterests = receivedInterests.filter(i => i.status === "pending");
  const acceptedInterests = receivedInterests.filter(i => i.status === "accepted");

  // Check subscription status
  const hasActiveSubscription = user?.subscription && user?.subscription_expires && 
    new Date(user.subscription_expires) > new Date();

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50/30">
      {/* Promo Banner */}
      {showPromoBanner && (
        <PromoBanner onClose={() => setShowPromoBanner(false)} />
      )}
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-rose-100/50" style={{ top: showPromoBanner ? '48px' : '0' }} data-testid="dashboard-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <div onClick={handleLogoClick} className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-logo">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/browse" className="text-rose-600 hover:text-rose-800 transition-colors font-medium" data-testid="nav-browse">
                {t('nav.browse')}
              </Link>
              <Link to="/chat" className="text-rose-600 hover:text-rose-800 transition-colors font-medium" data-testid="nav-chat">
                {t('nav.chat')}
              </Link>
              <Link to="/report" className="text-rose-600 hover:text-rose-800 transition-colors font-medium" data-testid="nav-report">
                {t('nav.report')}
              </Link>
              <Link to="/settings" className="text-rose-600 hover:text-rose-800 transition-colors font-medium" data-testid="nav-settings">
                {t('nav.settings')}
              </Link>
              <button onClick={handleLogout} className="text-rose-600 hover:text-red-600 transition-colors font-medium" data-testid="nav-logout">
                {t('nav.logout')}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-rose-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-rose-100 py-4 px-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to="/browse" className="text-rose-600 py-2 font-medium">
                {t('nav.browse')}
              </Link>
              <Link to="/chat" className="text-rose-600 py-2 font-medium">
                {t('nav.chat')}
              </Link>
              <Link to="/report" className="text-rose-600 py-2 font-medium">
                {t('nav.reportIssue')}
              </Link>
              <Link to="/settings" className="text-rose-600 py-2 font-medium">
                {t('nav.settings')}
              </Link>
              <div className="py-2">
                <LanguageCurrencySelector />
              </div>
              <button onClick={handleLogout} className="text-red-600 py-2 text-left font-medium">
                {t('nav.logout')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-44 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-rose-900 mb-2" data-testid="welcome-heading">
            {t('dashboard.greeting')}, {user?.first_name}
          </h1>
          <p className="text-rose-600">{t('dashboard.subtitle')}</p>
        </div>

        {/* Subscription Banner */}
        {!hasActiveSubscription && (
          <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 rounded-2xl p-6 mb-8 text-white shadow-lg shadow-rose-500/20" data-testid="subscription-banner">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-xl mb-1">{t('dashboard.unlockAccess')}</h3>
                <p className="text-rose-100 text-sm">{t('dashboard.unlockDesc')}</p>
              </div>
              <Button 
                onClick={() => navigate("/pricing")}
                className="bg-white text-rose-600 hover:bg-rose-50 rounded-full px-6 shadow-lg"
                data-testid="upgrade-btn"
              >
                {t('common.viewPlans')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-rose-100 shadow-sm" data-testid="stat-received">
            <div className="mb-2">
              <span className="text-3xl font-light text-rose-300">01</span>
            </div>
            <div className="stat-number text-2xl">{pendingInterests.length}</div>
            <p className="text-rose-600 text-sm">{t('dashboard.pendingInterests')}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-rose-100 shadow-sm" data-testid="stat-accepted">
            <div className="mb-2">
              <span className="text-3xl font-light text-amber-300">02</span>
            </div>
            <div className="stat-number text-2xl">{acceptedInterests.length}</div>
            <p className="text-rose-600 text-sm">{t('dashboard.mutualMatches')}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-rose-100 shadow-sm" data-testid="stat-sent">
            <div className="mb-2">
              <span className="text-3xl font-light text-purple-300">03</span>
            </div>
            <div className="stat-number text-2xl">{sentInterests.length}</div>
            <p className="text-rose-600 text-sm">{t('dashboard.interestsSent')}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-rose-100 shadow-sm" data-testid="stat-subscription">
            <div className="mb-2">
              <span className="text-3xl font-light text-emerald-300">04</span>
            </div>
            <div className="text-lg font-semibold text-rose-900">
              {hasActiveSubscription ? (
                <span className="badge-premium">{user?.subscription?.toUpperCase()}</span>
              ) : (
                <span className="text-rose-400">Free</span>
              )}
            </div>
            <p className="text-rose-600 text-sm">{t('dashboard.subscription')}</p>
          </div>
        </div>

        {/* Quote of the Day */}
        {quote && (
          <div className="quote-card mb-8" data-testid="quote-card">
            <div className="flex items-start gap-4">
              <span className="text-4xl text-rose-200 font-serif leading-none">"</span>
              <div>
                <p className="text-white text-lg leading-relaxed mb-3">{quote.text}</p>
                <p className="text-rose-200 text-sm font-medium">— {quote.source}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Received Interests */}
          <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden" data-testid="received-interests-section">
            <div className="p-6 border-b border-rose-100">
              <h2 className="font-serif text-xl text-rose-900">{t('dashboard.interestsReceived')}</h2>
              <p className="text-rose-500 text-sm">{t('dashboard.interestsReceivedDesc')}</p>
            </div>

            {loading ? (
              <div className="p-6">
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full skeleton" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 skeleton rounded" />
                        <div className="h-3 w-24 skeleton rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : pendingInterests.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl text-rose-300 font-serif">♡</span>
                </div>
                <p className="text-rose-600">No pending interests yet</p>
                <p className="text-rose-400 text-sm mt-1">{t('dashboard.noPendingHint')}</p>
              </div>
            ) : (
              <div className="divide-y divide-rose-100">
                {pendingInterests.slice(0, 5).map((interest) => (
                  <div key={interest.id} className="p-4 hover:bg-rose-50 transition-colors" data-testid={`interest-${interest.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-rose-200 overflow-hidden">
                        {interest.sender?.photos?.[0] ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${interest.sender.photos[0]}`}
                            alt={interest.sender?.first_name}
                            className={`w-full h-full object-cover ${!hasActiveSubscription ? 'photo-blur' : ''}`}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-rose-400 bg-rose-100">
                            <span className="text-xl font-serif">{interest.sender?.first_name?.[0] || "?"}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-rose-900">
                          {interest.sender?.first_name}, {interest.sender?.country}
                        </h3>
                        <p className="text-rose-500 text-sm">
                          {interest.sender?.profession || t('common.notSpecified')} • {interest.sender?.muslim_type || t('common.muslim')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleAcceptInterest(interest.id)}
                          className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full"
                          data-testid={`accept-${interest.id}`}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeclineInterest(interest.id)}
                          className="border-rose-300 text-rose-700 rounded-full"
                          data-testid={`decline-${interest.id}`}
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mutual Matches / Accepted */}
          <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden" data-testid="matches-section">
            <div className="p-6 border-b border-rose-100">
              <h2 className="font-serif text-xl text-rose-900">{t('dashboard.yourMatches')}</h2>
              <p className="text-rose-500 text-sm">{t('dashboard.yourMatchesDesc')}</p>
            </div>

            {loading ? (
              <div className="p-6">
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full skeleton" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 skeleton rounded" />
                        <div className="h-3 w-24 skeleton rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : acceptedInterests.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl text-rose-300 font-serif">∞</span>
                </div>
                <p className="text-rose-600">No matches yet</p>
                <p className="text-rose-400 text-sm mt-1">Accept interests to make connections</p>
              </div>
            ) : (
              <div className="divide-y divide-rose-100">
                {acceptedInterests.map((interest) => (
                  <div key={interest.id} className="p-4 hover:bg-rose-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-rose-200 overflow-hidden">
                        {interest.sender?.photos?.[0] ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${interest.sender.photos[0]}`}
                            alt={interest.sender?.first_name}
                            className={`w-full h-full object-cover ${!hasActiveSubscription ? 'photo-blur' : ''}`}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-rose-400 bg-rose-100">
                            <span className="text-xl font-serif">{interest.sender?.first_name?.[0] || "?"}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-rose-900">{interest.sender?.first_name}</h3>
                        <p className="text-rose-500 text-sm">{interest.sender?.country}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/profile/${interest.sender?.id}`)}
                        className="bg-rose-100 text-rose-700 hover:bg-rose-200 rounded-full"
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button 
            onClick={() => navigate("/browse")}
            className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6 shadow-lg shadow-rose-500/30"
            data-testid="browse-profiles-btn"
          >
            {t('common.browseProfiles')}
          </Button>
          <Button 
            onClick={() => navigate("/chat")}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full px-6"
            data-testid="view-chats-btn"
          >
            {t('common.viewChats')}
          </Button>
          <Button 
            onClick={() => navigate("/report")}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-full px-6"
            data-testid="report-issue-btn"
          >
            {t('common.reportIssue')}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
